// One place that knows the envelope: { ok, data } or { ok, error }.
export type Envelope<T> = { ok: true; data: T } | { ok: false; error: string };

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/**
 * Carry a page-load's forced-refresh flag onto its data request.
 *
 * `?fresh=1` means "ignore the price TTL for this view" (a test affordance the server gates to a dev
 * instance or an admin). The shell honours it in the boot payload; a client-side navigation has to pass
 * it on, or the same page would behave differently depending on how the reader arrived at it.
 */
export function withFresh(path: string): string {
  if (new URLSearchParams(location.search).get('fresh') !== '1') return path;
  return path + (path.includes('?') ? '&' : '?') + 'fresh=1';
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  let body: Envelope<T> | null = null;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    body = null;
  }
  if (!res.ok || !body || !body.ok) {
    const message = body && !body.ok ? body.error : `HTTP ${res.status}`;
    throw new ApiError(res.status, message);
  }
  return body.data;
}

/**
 * The page's own payload, when the shell was served with one.
 *
 * The SPA fetches after mount, which means the first paint has an empty table and a spinner — invisible to a
 * reader for a moment, but visible to anything that reads the document right after `load`. The shell carries the
 * payload for the page it served, so a query can start with data and still be refetched as usual.
 */
export function bootPage<T>(key: string): T | undefined {
  const b = (window as unknown as { __BOOT__?: { page?: { key: string; data: unknown } } }).__BOOT__;
  return b && b.page && b.page.key === key ? (b.page.data as T) : undefined;
}
